import { useCallback, useRef } from 'react';
import usePusher from '../../../helpers/hooks/usePusher';
import type { Channel } from 'pusher-js';

export type ThinkingEvent = {
  type: 'thinking';
  stage: string;
  text: string;
  user_message_id: number;
  seq?: number;
};

export type StreamChunkBlock = {
  id: string;
  block_type: string;
  html: string;
};

export type ChunkEvent = {
  type: 'chunk';
  html: string;
  blocks?: StreamChunkBlock[];
  user_message_id: number;
  seq?: number;
};

export type ScriptFinalization = {
  run_scripts_after_render?: boolean;
  script_count?: number;
  script_hashes?: string[];
};

export type DoneEvent = {
  type: 'done';
  message_id: number | null;
  user_message_id: number;
  token_usage?: unknown;
  /** Post-turn token balance pushed by the orchestrator (O8). Null for demo
   *  conversations; useful for optimistic meter updates before the full
   *  HTTP refresh lands. */
  balance_remaining?: number | null;
  error?: string;
  fetch_required?: boolean;
  script_finalization?: ScriptFinalization;
  stream_stats?: Record<string, unknown>;
  seq?: number;
};

export type StreamEvent = ThinkingEvent | ChunkEvent | DoneEvent;

interface PusherStreamHandlers {
  onThinking: (event: ThinkingEvent) => void;
  onChunk: (event: ChunkEvent) => void;
  onDone: (event: DoneEvent) => void;
  onError: (err: Error) => void;
}

/**
 * Subscribes to a Pusher channel and routes V2 stream events
 * (thinking / chunk / done) to the provided handlers.
 *
 * Only events matching the given `userMessageId` are forwarded,
 * so concurrent messages on the same conversation channel are safe.
 *
 * The handler auto-unbinds on the `done` event.
 */
export const usePusherStream = () => {
  const { pusherInstance } = usePusher();
  const channelRef = useRef<Channel | null>(null);
  const handlerRef = useRef<((data: StreamEvent) => void) | null>(null);
  const eventNameRef = useRef<string | null>(null);

  const startListening = useCallback(
    (
      channelName: string,
      eventName: string,
      userMessageId: number,
      handlers: PusherStreamHandlers
    ) => {
      stopListening();

      if (!pusherInstance) {
        handlers.onError(new Error('Pusher is not initialised'));
        return;
      }

      const channel = pusherInstance.subscribe(channelName);
      channelRef.current = channel;
      eventNameRef.current = eventName;

      const handler = (data: StreamEvent) => {
        if (data.user_message_id !== userMessageId) return;

        switch (data.type) {
          case 'thinking':
            handlers.onThinking(data as ThinkingEvent);
            break;
          case 'chunk':
            handlers.onChunk(data as ChunkEvent);
            break;
          case 'done':
            handlers.onDone(data as DoneEvent);
            unbindHandler();
            break;
        }
      };

      handlerRef.current = handler;
      channel.bind(eventName, handler);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pusherInstance]
  );

  const unbindHandler = useCallback(() => {
    if (channelRef.current && handlerRef.current && eventNameRef.current) {
      channelRef.current.unbind(eventNameRef.current, handlerRef.current);
    }
    handlerRef.current = null;
    eventNameRef.current = null;
  }, []);

  const stopListening = useCallback(() => {
    unbindHandler();
    channelRef.current = null;
  }, [unbindHandler]);

  return { startListening, stopListening };
};
